import { useMemo, useState } from 'react';
import { Activity, AlertTriangle, Check, Info, Stethoscope } from 'lucide-react';

export type SGRQDomainKey = 'symptoms' | 'activity' | 'impact';

export interface SGRQResultPayload {
  symptoms: number;
  activity: number;
  impact: number;
  total: number;
  domains: Record<SGRQDomainKey, { score: number }>;
  totalMethod: 'manual';
  interpretation: string;
  clinicalNotes: string[];
  finishedAt?: string;
}

interface SGRQTestProps {
  initial?: Partial<SGRQResultPayload>;
  onCancel?: () => void;
  onNext: (result: SGRQResultPayload) => void;
}

export const SGRQ_DOMAIN_LABELS: Record<SGRQDomainKey, string> = {
  symptoms: 'Sintomas',
  activity: 'Atividade',
  impact: 'Impacto',
};

const SGRQ_DOMAIN_HELPERS: Record<SGRQDomainKey, string> = {
  symptoms: 'Frequência e intensidade dos sintomas respiratórios.',
  activity: 'Limitações provocadas pela dispneia nas atividades do dia a dia.',
  impact: 'Efeito psicossocial da doença respiratória na rotina.',
};

const formatScoreInput = (value: string): string => value.replace(',', '.');

const parseScoreInput = (value: string): number | null => {
  const parsed = Number.parseFloat(formatScoreInput(value));
  if (!Number.isFinite(parsed)) return null;
  return Math.max(0, Math.min(100, Number(parsed.toFixed(1))));
};

const getSGRQSeverityLabel = (total: number | null): string => {
  if (total == null) return '—';
  return 'Escore SGRQ (0–100)';
};

const getDominantDomain = (scores: Record<SGRQDomainKey, number | null>): { key: SGRQDomainKey; score: number } | null => {
  const candidates = (Object.keys(scores) as SGRQDomainKey[])
    .map((key) => ({ key, score: scores[key] }))
    .filter((item): item is { key: SGRQDomainKey; score: number } => typeof item.score === 'number')
    .sort((a, b) => b.score - a.score);

  return candidates[0] ?? null;
};

const getSGRQInterpretationText = (scores: Record<SGRQDomainKey, number | null>, total: number | null): string => {
  if (total == null) return 'Informe os escores consolidados do SGRQ para liberar a interpretação clínica.';

  const dominant = getDominantDomain(scores);
  const dominantText = dominant ? `O maior comprometimento aparece em ${SGRQ_DOMAIN_LABELS[dominant.key].toLowerCase()}.` : '';

  return `O escore total foi ${total.toFixed(1)}/100; valores maiores representam pior qualidade de vida relacionada à saúde. ${dominantText} Não foi aplicada divisão automática em quartis de gravidade.`.trim();
};

const buildClinicalNotes = (scores: Record<SGRQDomainKey, number | null>, total: number | null): string[] => {
  const notes: string[] = [];
  const dominant = getDominantDomain(scores);

  notes.push('Pontuações variam de 0 a 100; valores mais altos indicam pior qualidade de vida relacionada à saúde.');
  notes.push('Diferenças de 4 pontos ou mais costumam ser clinicamente significativas em reavaliações seriadas.');

  if (dominant) {
    notes.push(`Domínio dominante nesta aplicação: ${SGRQ_DOMAIN_LABELS[dominant.key]} (${dominant.score.toFixed(1)}).`);
  }

  if (typeof total === 'number') notes.push('O resultado basal é apresentado sem faixa automática de gravidade.');

  return notes;
};

function DomainInputCard({
  title,
  helper,
  value,
  onChange,
}: {
  title: string;
  helper: string;
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="text-xs font-black uppercase tracking-[0.2em] text-slate-500">{title}</div>
      <p className="mt-2 text-sm leading-relaxed text-slate-600">{helper}</p>
      <div className="mt-4">
        <label className="mb-1 block text-sm font-medium text-slate-700">Score (0 a 100)</label>
        <input
          type="number"
          min={0}
          max={100}
          step="0.1"
          value={value}
          onChange={(event) => onChange(event.target.value)}
          className="w-full rounded-xl border-slate-300 px-4 py-3 text-lg font-semibold text-slate-900 focus:border-primary-500 focus:ring-primary-500"
          placeholder="0,0"
        />
      </div>
    </div>
  );
}

export default function SGRQTest({ initial, onCancel, onNext }: SGRQTestProps) {
  const [symptomsInput, setSymptomsInput] = useState(
    typeof initial?.symptoms === 'number' ? initial.symptoms.toFixed(1) : '',
  );
  const [activityInput, setActivityInput] = useState(
    typeof initial?.activity === 'number' ? initial.activity.toFixed(1) : '',
  );
  const [impactInput, setImpactInput] = useState(
    typeof initial?.impact === 'number' ? initial.impact.toFixed(1) : '',
  );
  const [totalInput, setTotalInput] = useState(
    typeof initial?.total === 'number' ? initial.total.toFixed(1) : '',
  );

  const symptoms = parseScoreInput(symptomsInput);
  const activity = parseScoreInput(activityInput);
  const impact = parseScoreInput(impactInput);
  const total = parseScoreInput(totalInput);

  const domainScores = useMemo<Record<SGRQDomainKey, number | null>>(
    () => ({
      symptoms,
      activity,
      impact,
    }),
    [activity, impact, symptoms],
  );

  const dominantDomain = getDominantDomain(domainScores);
  const estimatedAverage = useMemo(() => {
    const values = [symptoms, activity, impact].filter((value): value is number => typeof value === 'number');
    if (values.length !== 3) return null;
    return Number((values.reduce((sum, value) => sum + value, 0) / values.length).toFixed(1));
  }, [activity, impact, symptoms]);

  const interpretation = getSGRQInterpretationText(domainScores, total);
  const clinicalNotes = buildClinicalNotes(domainScores, total);
  const allScoresReady = [symptoms, activity, impact, total].every((value) => typeof value === 'number');

  return (
    <div className="space-y-5">
      <div className="rounded-2xl border border-blue-200 bg-blue-50 p-4 text-blue-900">
        <div className="flex items-start gap-3">
          <Info className="mt-0.5 h-5 w-5" />
          <div className="space-y-2 text-sm leading-relaxed">
            <p className="font-semibold">SGRQ — lançamento estruturado</p>
            <p>
              Informe os escores finais já calculados do <strong>Saint George Respiratory Questionnaire</strong>.
              Neste corte da V2, o sistema registra os domínios e o total consolidados, sem reproduzir o questionário
              proprietário item a item.
            </p>
          </div>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-4">
        <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-5 shadow-sm md:col-span-2">
          <div className="flex items-center gap-2 text-emerald-800">
            <Stethoscope className="h-4 w-4" />
            <span className="text-xs font-black uppercase tracking-[0.25em]">Resumo global</span>
          </div>
          <div className="mt-3 text-4xl font-black text-slate-900">{total != null ? total.toFixed(1) : '—'}</div>
          <div className="mt-2 inline-flex rounded-full bg-white/80 px-3 py-1 text-xs font-bold text-slate-700 shadow-sm">
            {getSGRQSeverityLabel(total)}
          </div>
          <p className="mt-3 text-sm leading-relaxed text-slate-600">{interpretation}</p>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="text-xs font-black uppercase tracking-[0.22em] text-slate-500">Média dos domínios</div>
          <div className="mt-3 text-3xl font-black text-slate-900">{estimatedAverage != null ? estimatedAverage.toFixed(1) : '—'}</div>
          <p className="mt-2 text-sm text-slate-600">Apenas referência visual. O total deve ser informado conforme o cálculo validado do instrumento.</p>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="text-xs font-black uppercase tracking-[0.22em] text-slate-500">Domínio dominante</div>
          <div className="mt-3 text-xl font-bold text-slate-900">
            {dominantDomain ? SGRQ_DOMAIN_LABELS[dominantDomain.key] : '—'}
          </div>
          <p className="mt-2 text-sm text-slate-600">
            {dominantDomain ? `${dominantDomain.score.toFixed(1)} pontos` : 'Preencha os domínios para visualizar.'}
          </p>
        </div>
      </div>

      <div className="grid gap-4 xl:grid-cols-2">
        <DomainInputCard
          title="Sintomas"
          helper={SGRQ_DOMAIN_HELPERS.symptoms}
          value={symptomsInput}
          onChange={setSymptomsInput}
        />
        <DomainInputCard
          title="Atividade"
          helper={SGRQ_DOMAIN_HELPERS.activity}
          value={activityInput}
          onChange={setActivityInput}
        />
        <DomainInputCard
          title="Impacto"
          helper={SGRQ_DOMAIN_HELPERS.impact}
          value={impactInput}
          onChange={setImpactInput}
        />

        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="text-xs font-black uppercase tracking-[0.2em] text-slate-500">Pontuação total</div>
          <p className="mt-2 text-sm leading-relaxed text-slate-600">
            Informe o score total do SGRQ já calculado pelo método validado. Quanto maior a pontuação, pior a qualidade
            de vida respiratória percebida.
          </p>
          <div className="mt-4">
            <label className="mb-1 block text-sm font-medium text-slate-700">Score total (0 a 100)</label>
            <input
              type="number"
              min={0}
              max={100}
              step="0.1"
              value={totalInput}
              onChange={(event) => setTotalInput(event.target.value)}
              className="w-full rounded-xl border-slate-300 px-4 py-3 text-lg font-semibold text-slate-900 focus:border-primary-500 focus:ring-primary-500"
              placeholder="0,0"
            />
          </div>
          {estimatedAverage != null && total != null && Math.abs(estimatedAverage - total) > 10 && (
            <div className="mt-4 flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
              <AlertTriangle className="mt-0.5 h-4 w-4" />
              <p>
                A média simples dos domínios ({estimatedAverage.toFixed(1)}) difere do total informado ({total.toFixed(1)}).
                Isso pode ocorrer pelo método original de ponderação do instrumento.
              </p>
            </div>
          )}
        </div>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="mb-3 flex items-center gap-2 text-slate-800">
          <Activity className="h-4 w-4 text-primary-600" />
          <h4 className="font-semibold">Resumo clínico automático</h4>
        </div>
        <ul className="space-y-2 text-sm text-slate-700">
          {clinicalNotes.map((item) => (
            <li key={item} className="rounded-xl bg-slate-50 px-4 py-3 leading-relaxed">
              {item}
            </li>
          ))}
        </ul>
      </div>

      <div className="flex items-center justify-between">
        <button type="button" className="text-sm text-gray-600 hover:text-gray-800" onClick={onCancel}>
          Cancelar
        </button>
        <button
          type="button"
          className="inline-flex items-center rounded-md bg-green-600 px-4 py-2 text-sm font-medium text-white hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-50"
          disabled={!allScoresReady}
          onClick={() =>
            allScoresReady &&
            onNext({
              symptoms: symptoms!,
              activity: activity!,
              impact: impact!,
              total: total!,
              domains: {
                symptoms: { score: symptoms! },
                activity: { score: activity! },
                impact: { score: impact! },
              },
              totalMethod: 'manual',
              interpretation,
              clinicalNotes,
              finishedAt: new Date().toISOString(),
            })
          }
        >
          Concluir SGRQ
          <Check className="ml-2 h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
